import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  createHonorarioContrato,
  deleteHonorarioContrato,
  getHonorarioContratos,
  updateHonorarioContrato,
} from '../services/honorarioService';
import HonorariosContratoDialog from './HonorariosContratoDialog';

export default function HonorariosContratosTab({ users, professionals, clinics, specialties, branches, tiposPrestacion, aranceles }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getHonorarioContratos();
      setContracts(data);
    } catch (e) {
      console.error('Error cargando contratos:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contracts;
    return contracts.filter((c) => {
      const haystack = `${c.professionalName || ''} ${c.userName || ''} ${c.userEmail || ''} ${c.clinicName || ''} ${c.specialtyName || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [contracts, search]);

  const handleSave = async (data) => {
    if (editing) {
      await updateHonorarioContrato(editing.id, data);
    } else {
      await createHonorarioContrato(data);
    }
    setDialogOpen(false);
    setEditing(null);
    await load();
  };

  const handleEdit = (c) => {
    setEditing(c);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este contrato? Esta acción no se puede deshacer.')) return;
    try {
      await deleteHonorarioContrato(id);
      await load();
    } catch (e) {
      console.error('Error eliminando contrato:', e);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Contratos de honorarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crea contratos por clínica y especialidad, definiendo el pago por cada prestación del arancel.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar contrato"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ borderRadius: 3 }}
          >
            Añadir contrato
          </Button>
        </Box>
      </Box>

      {filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary" gutterBottom>
            No hay contratos registrados
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreate} sx={{ mt: 2 }}>
            Crear primer contrato
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Profesional</strong></TableCell>
                <TableCell><strong>Usuario</strong></TableCell>
                <TableCell><strong>Sucursal</strong></TableCell>
                <TableCell><strong>Clínica</strong></TableCell>
                <TableCell><strong>Especialidad</strong></TableCell>
                <TableCell align="center"><strong>Servicios</strong></TableCell>
                <TableCell align="center"><strong>Contrato</strong></TableCell>
                <TableCell align="center"><strong>Vigente</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.professionalName || '—'}</TableCell>
                  <TableCell>{c.userName || c.userEmail || '—'}</TableCell>
                  <TableCell>{c.branchName || '—'}</TableCell>
                  <TableCell>{c.clinicName || '—'}</TableCell>
                  <TableCell>{c.specialtyName || '—'}</TableCell>
                  <TableCell align="center">
                    <Chip label={`${(c.services || []).length}`} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={c.status === 'vigente' ? 'Vigente' : (c.status === 'pendiente' ? 'Pendiente' : c.status)}
                      size="small"
                      color={c.status === 'vigente' ? 'success' : (c.status === 'pendiente' ? 'warning' : 'default')}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={c.active !== false ? 'Sí' : 'No'}
                      size="small"
                      color={c.active !== false ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(c)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => handleDelete(c.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <HonorariosContratoDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={handleSave}
        users={users}
        professionals={professionals}
        clinics={clinics}
        specialties={specialties}
        branches={branches}
        tiposPrestacion={tiposPrestacion}
        aranceles={aranceles}
        initialData={editing}
      />
    </Box>
  );
}
